import { Device, DeviceType, Icons, OperationMode, LogLevel, SensorConfiguration, VIDEO_QUALITY_PROFILES } from "@app/shared";
import { OfflineModeInfo } from "@app/shared/src/components/OfflineModeInfo";
import { TakeTargetPhotosInfo } from "@app/shared/src/components/TakeTargetPhotosInfo";
import { MouseEventHandler, useEffect, useMemo, useState } from "react";
import { sanitizeDnsLabel, SELF_SCHEDULED_DEVICE_PHOTO_FIRMWARE_VERSION } from "../../helpers";
import { ModelHelpers } from "../../hooks";
import { EditSensorConfig } from "./EditSensorConfig";
import { HeaderWithSeparator } from "./HeaderWithSeparator";
import { CheckboxInput, EnumSelect, Input, InputRow, NumericInput, Select } from "./inputs";
import { SliderInput } from "./inputs/SliderInput";

type VideoProfile = {
    label: string;
    key: string;
    bitrateBps: number;
    resolutionX: number;
    resolutionY: number;
    fps: number;
};

const bitrateLabel = (bps: number) => (bps >= 1_000_000 ? `${bps / 1_000_000}Mb/s` : `${bps / 1_000}Kb/s`);

// The ladder itself lives in @app/shared so the fleet console offers these exact rungs rather than
// a parallel set of its own; "Custom" is this editor's sentinel for values matching no rung.
const videoProfiles: VideoProfile[] = [
    ...VIDEO_QUALITY_PROFILES.map((p) => ({
        label: `${p.name} (${p.resolutionX}x${p.resolutionY} ${p.fps}fps ${bitrateLabel(p.bitrateBps)})`,
        key: p.key,
        bitrateBps: p.bitrateBps,
        resolutionX: p.resolutionX,
        resolutionY: p.resolutionY,
        fps: p.fps,
    })),
    { label: "Custom", key: "custom", bitrateBps: 0, resolutionX: 0, resolutionY: 0, fps: 0 },
];

// Effective firmware logging defaults, mirrored from Shared/Models/Api/v1/DeviceSettings.cs
// (DefaultLogRetentionDays / DefaultLogRetentionLevel). Shown as placeholders so an unset (null)
// value communicates the real default the device will apply instead of an ambiguous blank.
const DEFAULT_LOG_RETENTION_DAYS = 30;
const DEFAULT_LOG_RETENTION_LEVEL_LABEL = "Information";

function detectProfile(bitrate: number | undefined, resX: number | undefined, resY: number | undefined, fps: number | undefined): string {
    const match = videoProfiles.find(p => p.key !== "custom" && p.bitrateBps === bitrate && p.resolutionX === resX && p.resolutionY === resY && p.fps === fps);
    return match?.key ?? "custom";
}

type EditDeviceDetailsProps = {
    device: Partial<Device>;
    helpers: ModelHelpers<Device>;
    initialSection?: DeviceConfigSection;
    onChange?: () => void;
    onSubmit?: MouseEventHandler;
    onCancel?: () => void;
};
export type DeviceConfigSection = "General" | "Network" | "Video" | "Target Photos" | "Radar";
export const EditDeviceDetails: React.FC<EditDeviceDetailsProps> = ({ device, helpers, initialSection = "General", onChange, onCancel, onSubmit }) => {
    const sections: DeviceConfigSection[] = ["General", "Network", "Video", "Target Photos", "Radar"];
    const [activeSection, setActiveSection] = useState<DeviceConfigSection>(initialSection);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [sensorConfigError, setSensorConfigError] = useState<string | undefined>();
    const [customChirpChosen, setCustomChirpChosen] = useState(false);
    const [isSidebarOpen, setIsSidebarOpen] = useState(false);

    const handleSubmit: MouseEventHandler = async (event) => {
        if (sensorConfigError) {
            // The message is shown in the Radar section; take the user to it rather than saving.
            event.preventDefault();
            setActiveSection("Radar");
            return;
        }
        setIsSubmitting(true);
        try {
            if (onSubmit) {
                await onSubmit(event);
            }
        } catch { }
        setIsSubmitting(false);
    };

    const handleFieldChange = (field: keyof Device, value: any) => {
        let processedValue = value;

        if (field === "headingDegrees" && typeof value === "number") {
            processedValue = Math.max(-360, Math.min(360, value));
        }

        helpers.updateModel(field, processedValue);
        onChange?.();
    };

    if (!sections.find(x => x === activeSection)) {
        setActiveSection(sections[0]);
    }
    useEffect(() => {
        const handleEscape = (e: KeyboardEvent) => {
            if (e.key === "Escape" && isSidebarOpen) {
                setIsSidebarOpen(false);
            }
        };

        document.addEventListener("keydown", handleEscape);
        return () => document.removeEventListener("keydown", handleEscape);
    }, [isSidebarOpen]);

    return (
        <div className="w-full h-[72vh] rounded-2xl bg-white shadow-xl overflow-hidden relative">
            <div className="flex h-full">
                {!isSidebarOpen && (
                    <button onClick={() => setIsSidebarOpen(true)} className="md:hidden absolute top-4 left-4 z-50 p-2 rounded-md bg-brand-primary text-white hover:bg-brand-primary-darkened transition-colors" aria-label="Toggle menu">
                        <Icons.Bars className="w-6 h-6" />
                    </button>
                )}

                {isSidebarOpen && <div className="md:hidden absolute inset-0 bg-black bg-opacity-50 z-30 rounded-2xl" onClick={() => setIsSidebarOpen(false)} />}

                <div
                    className={`
                    w-56 bg-brand-secondary-darkened p-4 space-y-2 z-40
                    absolute md:relative h-full rounded-l-2xl
                    transition-transform duration-300 ease-in-out
                    ${isSidebarOpen ? "translate-x-0" : "-translate-x-full md:translate-x-0"}
                `}
                >
                    <div className="flex justify-between items-center mb-4">
                        <h2 className="text-white font-semibold text-lg pl-2">Settings</h2>
                        {/* Mobile Close Button */}
                        <button onClick={() => setIsSidebarOpen(false)} className="md:hidden text-white p-1 bg-brand-primary hover:bg-brand-primary-darkened rounded transition-colors" aria-label="Close menu">
                            <Icons.Times className="w-5 h-5" />
                        </button>
                    </div>
                    {sections.map((section) => (
                        <button
                            key={section}
                            onClick={() => {
                                setActiveSection(section);
                                setIsSidebarOpen(false);
                            }}
                            className={`w-full text-left px-4 py-3 rounded-md transition-all duration-200 border-none h-[unset] m-[unset]
                            ${activeSection === section ? "bg-brand-primary text-white shadow-md transform translate-x-1" : "text-gray-200 bg-brand-secondary-darkened hover:bg-brand-secondary-lightened"}
                        `}
                        >
                            {section}
                        </button>
                    ))}
                </div>

                <div className="flex-1 flex flex-col overflow-hidden">
                    <div className="flex-1 p-6 overflow-y-auto pt-16 md:pt-6">
                        {activeSection === "General" && (
                            <>
                                <HeaderWithSeparator>General</HeaderWithSeparator>
                                <InputRow>
                                    <div className="flex-1">
                                        <Input label="Name" {...helpers.bindingsFor("name", onChange)} />
                                        {device.name && (
                                            <p className="mt-1 text-xs text-muted-body-text">
                                                mDNS address: <span className="font-mono">{sanitizeDnsLabel(device.name)}.local</span>
                                                {device.name !== sanitizeDnsLabel(device.name) && (
                                                    <span className="ml-1 text-brand-primary">(spaces/special chars sanitized for DNS)</span>
                                                )}
                                            </p>
                                        )}
                                    </div>
                                </InputRow>
                                <InputRow>
                                    <Input label="Description" {...helpers.bindingsFor("description", onChange)} />
                                </InputRow>
                                <InputRow>
                                    <EnumSelect enumType={DeviceType} label="Device Type" {...helpers.bindingsFor("type", onChange)} />
                                </InputRow>
                                <InputRow>
                                    <EnumSelect enumType={OperationMode} label="Operation Mode" {...helpers.bindingsFor("mode", onChange)} />
                                </InputRow>

                                <InputRow>
                                    <Input label="Max History Per Second" {...helpers.bindingsFor("historyPerSecond", onChange)} />
                                </InputRow>
                                <InputRow>
                                    <CheckboxInput label="Always Stream Radar Data" checked={device.alwaysStreamBinaryData} onChange={(e) => handleFieldChange("alwaysStreamBinaryData", e.target.checked)} />
                                </InputRow>
                                {device.alwaysStreamBinaryData && (
                                    <>
                                        <InputRow>
                                            <Input label="Binary Stream Ip" {...helpers.bindingsFor("streamBinaryDestinationIp", onChange)} />
                                        </InputRow>
                                        <InputRow>
                                            <NumericInput label="Binary Stream Port" allowFloat={false} {...helpers.bindingsFor("streamBinaryDestinationPort", onChange)} />
                                        </InputRow>
                                    </>
                                )}

                                <InputRow>
                                    <span className="inline-flex items-center gap-1.5">
                                        <CheckboxInput label="Offline Mode" checked={device.offlineMode} onChange={(e) => handleFieldChange("offlineMode", e.target.checked)} />
                                        <OfflineModeInfo />
                                    </span>
                                </InputRow>
                                {device.facilityId && (
                                    <>
                                        <HeaderWithSeparator>Lot Location</HeaderWithSeparator>
                                        <InputRow>
                                            <div>
                                                <Input label="X Origin Offset (m)" {...helpers.bindingsFor("positionX", onChange)} />
                                            </div>
                                            <div>
                                                <Input label="Y Origin Offset (m)" {...helpers.bindingsFor("positionY", onChange)} />
                                            </div>
                                        </InputRow>
                                        <InputRow>
                                            <Input label="Heading (deg)" {...helpers.bindingsFor("headingDegrees", onChange)} />
                                        </InputRow>
                                        <InputRow>
                                            <Input label="Desired Tilt (deg)" {...helpers.bindingsFor("desiredTiltDegrees", onChange)} />
                                        </InputRow>
                                        <InputRow>
                                            <Input label="Height (m)" {...helpers.bindingsFor("height", onChange)} />
                                        </InputRow>
                                    </>
                                )}
                                {!device.facilityId && (
                                    <>
                                        <HeaderWithSeparator disabled>Lot Location (Device must be assigned to Location)</HeaderWithSeparator>
                                        <InputRow>
                                            <div>
                                                <Input disabled label="X Origin Offset (m)" {...helpers.bindingsFor("positionX")} />
                                            </div>
                                            <div>
                                                <Input disabled label="Y Origin Offset (m)" {...helpers.bindingsFor("positionY")} />
                                            </div>
                                        </InputRow>
                                        <InputRow>
                                            <Input disabled label="Heading (deg)" {...helpers.bindingsFor("headingDegrees")} />
                                        </InputRow>
                                        <InputRow>
                                            <Input disabled label="Desired Tilt (deg)" {...helpers.bindingsFor("desiredTiltDegrees")} />
                                        </InputRow>
                                        <InputRow>
                                            <Input disabled label="Height (m)" {...helpers.bindingsFor("height")} />
                                        </InputRow>
                                    </>
                                )}
                                <HeaderWithSeparator>Data Retention</HeaderWithSeparator>
                                <InputRow>
                                    <NumericInput label="Data Retention (days, 0 = until synced)" allowFloat={false} {...helpers.bindingsFor("dataRetentionDays", onChange)} />
                                </InputRow>
                                <HeaderWithSeparator>Health Thresholds</HeaderWithSeparator>
                                <InputRow>
                                    <NumericInput label="Max Minutes Between Targets (empty = 1440)" allowFloat={false} {...helpers.bindingsFor("maxMinutesBetweenTargets", onChange)} />
                                </InputRow>
                                <HeaderWithSeparator>Logging</HeaderWithSeparator>
                                <InputRow>
                                    <NumericInput label="Log Retention (days)" allowFloat={false} placeholder={`Default: ${DEFAULT_LOG_RETENTION_DAYS}`} {...helpers.bindingsFor("logRetentionDays", onChange)} />
                                </InputRow>
                                <InputRow>
                                    <EnumSelect enumType={LogLevel} label="Preserved Log Level" emptyOptionText={`Default: ${DEFAULT_LOG_RETENTION_LEVEL_LABEL}`} emptyOptionDisabled={false} {...helpers.bindingsFor("logRetentionLevel", onChange)} />
                                </InputRow>
                            </>
                        )}

                        {activeSection === "Network" && (
                            <>
                                <HeaderWithSeparator>Network</HeaderWithSeparator>
                                <InputRow>
                                    <CheckboxInput label="Enable DHCP" checked={device.enableDhcp} onChange={(e) => handleFieldChange("enableDhcp", e.target.checked)} />
                                </InputRow>
                                <InputRow>
                                    <Input placeholder="192.168.1.1/24" label="IP Address" {...helpers.bindingsFor("ipv4Address", onChange)} disabled={device.enableDhcp} />
                                </InputRow>
                                <InputRow>
                                    <Input label="Gateway Addresses" {...helpers.bindingsFor("gatewayAddresses", onChange)} disabled={device.enableDhcp} />
                                </InputRow>
                                <InputRow>
                                    <Input label="DNS Servers" {...helpers.bindingsFor("dnsServers", onChange)} disabled={device.enableDhcp} />
                                </InputRow>
                                <InputRow>
                                    <NumericInput label="Heartbeat Frequency (seconds)" allowFloat={false} {...helpers.bindingsFor("secondsBetweenHeartbeat", onChange)} />
                                </InputRow>
                                <InputRow>
                                    <Input label="NTP Servers" {...helpers.bindingsFor("ntpServers", onChange)} />
                                </InputRow>
                            </>
                        )}
                        {activeSection === "Video" && (
                            <VideoSection device={device} helpers={helpers} handleFieldChange={handleFieldChange} onChange={onChange} />
                        )}
                        {activeSection === "Target Photos" && (
                            <>
                                <HeaderWithSeparator>Target Photos</HeaderWithSeparator>
                                <InputRow>
                                    <span className="inline-flex items-center gap-1.5">
                                        <CheckboxInput label="Photograph Every Target" checked={device.takeTargetPhotos} onChange={(e) => handleFieldChange("takeTargetPhotos", e.target.checked)} />
                                        <TakeTargetPhotosInfo />
                                    </span>
                                </InputRow>
                                {/* Only the rate limit is specific to global capture. The rest shape every snapshot,
                                    including the ones event triggers take with Capture Photo while this is off. */}
                                {device.takeTargetPhotos && (
                                    <InputRow>
                                        <Input label="Min Seconds Between Photos" {...helpers.bindingsFor("minSecondsBetweenPhotos", onChange)} />
                                    </InputRow>
                                )}
                                <InputRow>
                                    <CheckboxInput label="Use PNG Format" checked={device.photoPNG} onChange={(e) => handleFieldChange("photoPNG", e.target.checked)} />
                                </InputRow>
                                <InputRow>
                                    <Input label="Resolution X" placeholder="1920" {...helpers.bindingsFor("snapshotResolutionX", onChange)} />
                                </InputRow>
                                <InputRow>
                                    <Input label="Resolution Y" placeholder="1080" {...helpers.bindingsFor("snapshotResolutionY", onChange)} />
                                </InputRow>
                                <InputRow>
                                    <NumericInput label="Photo Retention (days, 0 = until synced)" allowFloat={false} {...helpers.bindingsFor("targetPhotoRetentionDays", onChange)} />
                                </InputRow>
                                {!!!device.photoPNG && (
                                    <InputRow>
                                        <SliderInput min={1} max={100} placeholder="75" label="JPG Quality" {...helpers.bindingsFor("jpgQuality", onChange)} />
                                    </InputRow>
                                )}
                                <HeaderWithSeparator>Device Photo</HeaderWithSeparator>
                                <InputRow>
                                    <NumericInput label="Device Photo Refresh (minutes, empty = 120, 0 = off)" allowFloat={false} {...helpers.bindingsFor("devicePhotoUpdateIntervalMinutes", onChange)} />
                                </InputRow>
                                <p className="text-sm text-muted-body-text">
                                    How often the device retakes the camera photo shown on the dashboard. Firmware {SELF_SCHEDULED_DEVICE_PHOTO_FIRMWARE_VERSION} and later keeps this schedule itself; older firmware is asked by the server every two hours regardless.
                                </p>
                            </>
                        )}
                        {activeSection === "Radar" && (device.sensorConfig
                            ? <EditSensorConfig deviceMode={device.mode} config={device.sensorConfig} onChange={(newConfig) => handleFieldChange("sensorConfig", newConfig as SensorConfiguration)} onValidityChange={setSensorConfigError} customChirpChosen={customChirpChosen} onCustomChirpChosenChange={setCustomChirpChosen} />
                            // Say so rather than rendering a blank pane. A device only has a sensor
                            // config once it has reported one, and callers must hand us a model
                            // loaded by id (the device *list* endpoints omit it).
                            : <p className="text-muted-body-text">This device has no radar configuration to edit.</p>)}
                        {<helpers.ErrorSummary></helpers.ErrorSummary>}
                    </div>
                    {(onSubmit !== undefined || onCancel !== undefined) && (
                        <div className="border-t border-gray-200 bg-gray-50 p-4 sticky bottom-0">
                            <div className="max-w-4xl mx-auto w-full flex justify-end space-x-3">
                                <button type="submit" disabled={isSubmitting} onClick={handleSubmit} className={`px-4 py-2 text-sm font-medium text-white bg-brand-primary border border-transparent rounded-md hover:bg-brand-primary-darkened focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand-primary-darkened ${isSubmitting ? "disabled:opacity-50" : ""}`}>
                                    {isSubmitting ? "Saving..." : "Save"}
                                </button>
                                <button type="button" disabled={isSubmitting} onClick={onCancel} className={`px-4 py-2 text-sm font-medium text-white bg-brand-secondary border border-gray-300 rounded-md hover:bg-brand-secondary-darkened focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand-primary ${isSubmitting ? "disabled:opacity-50" : ""}`}>
                                    Cancel
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

const profileOptions = videoProfiles.map(p => ({ label: p.label, value: p.key }));

type VideoSectionProps = {
    device: Partial<Device>;
    helpers: ModelHelpers<Device>;
    handleFieldChange: (field: keyof Device, value: any) => void;
    onChange?: () => void;
};

const VideoSection: React.FC<VideoSectionProps> = ({ device, helpers, handleFieldChange, onChange }) => {
    const dvrProfile = useMemo(() => detectProfile(device.dvrBitrateBps, device.dvrResolutionX, device.dvrResolutionY, device.dvrFps), [device.dvrBitrateBps, device.dvrResolutionX, device.dvrResolutionY, device.dvrFps]);
    const streamProfile = useMemo(() => detectProfile(device.videoBitrateBps, device.videoResolutionX, device.videoResolutionY, device.videoFps), [device.videoBitrateBps, device.videoResolutionX, device.videoResolutionY, device.videoFps]);

    const applyDvrProfile = (key: string) => {
        const profile = videoProfiles.find(p => p.key === key);
        if (!profile || profile.key === "custom") return;
        handleFieldChange("dvrBitrateBps", profile.bitrateBps);
        handleFieldChange("dvrResolutionX", profile.resolutionX);
        handleFieldChange("dvrResolutionY", profile.resolutionY);
        handleFieldChange("dvrFps", profile.fps);
    };

    const applyStreamProfile = (key: string) => {
        const profile = videoProfiles.find(p => p.key === key);
        if (!profile || profile.key === "custom") return;
        handleFieldChange("videoBitrateBps", profile.bitrateBps);
        handleFieldChange("videoResolutionX", profile.resolutionX);
        handleFieldChange("videoResolutionY", profile.resolutionY);
        handleFieldChange("videoFps", profile.fps);
    };

    return (
        <>
            <HeaderWithSeparator>DVR Recording</HeaderWithSeparator>
            <InputRow>
                <CheckboxInput label="DVR Enabled" checked={device.enableDvrRecording} onChange={(e) => handleFieldChange("enableDvrRecording", e.target.checked)} />
            </InputRow>
            <InputRow>
                <CheckboxInput label="DVR Bounding Boxes" checked={device.dvrBoundingBoxes} onChange={(e) => handleFieldChange("dvrBoundingBoxes", e.target.checked)} />
            </InputRow>
            <InputRow>
                <Select label="DVR Profile" options={profileOptions} value={dvrProfile} onChange={(e) => applyDvrProfile(e.target.value)} />
            </InputRow>
            <InputRow>
                <NumericInput label="Bitrate (bps)" allowFloat={false} {...helpers.bindingsFor("dvrBitrateBps", onChange)} />
            </InputRow>
            <InputRow>
                <NumericInput label="Resolution X" allowFloat={false} {...helpers.bindingsFor("dvrResolutionX", onChange)} />
            </InputRow>
            <InputRow>
                <NumericInput label="Resolution Y" allowFloat={false} {...helpers.bindingsFor("dvrResolutionY", onChange)} />
            </InputRow>
            <InputRow>
                <NumericInput label="FPS" allowFloat={false} {...helpers.bindingsFor("dvrFps", onChange)} />
            </InputRow>
            <InputRow>
                <NumericInput label="Retention Hours" allowFloat={false} {...helpers.bindingsFor("dvrRetentionHours", onChange)} />
            </InputRow>

            <HeaderWithSeparator>Live Streaming</HeaderWithSeparator>
            <InputRow>
                <CheckboxInput label="Always Stream Video" checked={device.alwaysStreamVideo} onChange={(e) => handleFieldChange("alwaysStreamVideo", e.target.checked)} />
            </InputRow>
            <InputRow>
                <CheckboxInput label="Streaming Bounding Boxes" checked={device.videoBoundingBoxes} onChange={(e) => handleFieldChange("videoBoundingBoxes", e.target.checked)} />
            </InputRow>
            <InputRow>
                <Select label="Streaming Profile" options={profileOptions} value={streamProfile} onChange={(e) => applyStreamProfile(e.target.value)} />
            </InputRow>
            <InputRow>
                <NumericInput label="Bitrate (bps)" allowFloat={false} {...helpers.bindingsFor("videoBitrateBps", onChange)} />
            </InputRow>
            <InputRow>
                <NumericInput label="Resolution X" allowFloat={false} {...helpers.bindingsFor("videoResolutionX", onChange)} />
            </InputRow>
            <InputRow>
                <NumericInput label="Resolution Y" allowFloat={false} {...helpers.bindingsFor("videoResolutionY", onChange)} />
            </InputRow>
            <InputRow>
                <NumericInput label="FPS" allowFloat={false} {...helpers.bindingsFor("videoFps", onChange)} />
            </InputRow>
        </>
    );
};
